import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Cloud, Sunrise, Sunset, Clock, Loader2, Wand2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import TimePicker from "@/components/TimePicker";
import { Field } from "./helpers";

export function HeaderSection({ callSheet, onField, primaryLocation, totalDays = 1 }) {
  const [wxLoading, setWxLoading] = useState(false);

  // Auto-compute the "Day of" label from day_number + totalDays. We never expose it
  // as editable — single source of truth lives on the days[] array. Multi-day shoots
  // get "Day N of M"; single-day shoots leave it blank to avoid "Day 1 of 1" noise.
  const computedDayOf =
    totalDays > 1 ? `Day ${callSheet.day_number} of ${totalDays}` : "";

  // Keep the persisted day_of in sync with the computed value so every downstream
  // surface (PDF, mobile share, email subjects, admin confirmation emails) renders
  // the right label automatically.
  useEffect(() => {
    if (callSheet.day_of !== computedDayOf) {
      onField("day_of", computedDayOf);
    }
  }, [computedDayOf, callSheet.day_of, onField]);

  const handleFetchWeather = async () => {
    if (!callSheet.shoot_date) {
      toast.error("Set a shoot date first");
      return;
    }
    if (!primaryLocation || !primaryLocation.address) {
      toast.error("Add at least one location with an address");
      return;
    }
    setWxLoading(true);
    try {
      const res = await api.get("/weather/lookup", {
        params: { address: primaryLocation.address, date: callSheet.shoot_date },
      });
      const d = res.data;
      onField("weather", d.weather);
      if (d.sunrise) onField("sunrise", d.sunrise);
      if (d.sunset) onField("sunset", d.sunset);
      const loc = d.city_state || d.location || "";
      if (d.is_stale) {
        // Weather API was throttled but we had a recent-ish cached forecast —
        // surface the age so the producer knows to re-fetch closer to the shoot.
        const hrs = d.cached_hours_ago || 1;
        toast.warning(
          `Weather API is rate-limited — showing cached forecast from ${hrs}h ago. Try again in a few minutes for a live fetch.`
        );
      } else {
        toast.success(
          loc
            ? `Weather filled for ${loc} · ${d.summary}`
            : `Weather filled · ${d.summary}`
        );
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Couldn't fetch weather (may be too far out — forecast is ~16 days)"
      );
    } finally {
      setWxLoading(false);
    }
  };

  return (
    <section className="rounded-md border border-zinc-800 bg-zinc-900/60 p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-overline text-zinc-400">Shoot Details</div>
        <button
          type="button"
          data-testid="autofill-weather-btn"
          onClick={handleFetchWeather}
          disabled={wxLoading || !callSheet.shoot_date || !primaryLocation}
          title={
            !callSheet.shoot_date
              ? "Set a shoot date first"
              : !primaryLocation
                ? "Select a location first"
                : "Auto-fill weather, sunrise & sunset from primary location"
          }
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {wxLoading ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" /> Fetching…
            </>
          ) : (
            <>
              <Wand2 className="w-3 h-3" /> Auto-fill Weather
            </>
          )}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="Shoot Date">
          <Input
            data-testid="cs-shoot-date"
            type="date"
            value={callSheet.shoot_date || ""}
            onChange={(e) => onField("shoot_date", e.target.value)}
            className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
          />
        </Field>
        <Field label="Day of">
          <Input
            data-testid="cs-day-of"
            value={computedDayOf}
            readOnly
            tabIndex={-1}
            placeholder={totalDays > 1 ? "" : "Single-day shoot"}
            title="Auto-set from the day tabs — add or remove days at the top of the page to change"
            className="bg-zinc-950/60 border-zinc-800 text-zinc-400 cursor-not-allowed focus-visible:ring-0 focus-visible:ring-offset-0 select-none"
          />
        </Field>
        <Field label={<><Cloud className="w-3 h-3 inline mr-1" />Weather</>}>
          <Input
            data-testid="cs-weather"
            value={callSheet.weather || ""}
            onChange={(e) => onField("weather", e.target.value)}
            placeholder="55° / 48°"
            className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
          />
        </Field>
        <Field label="Director Contact">
          <Input
            data-testid="cs-director"
            value={callSheet.director_contact || ""}
            onChange={(e) => onField("director_contact", e.target.value)}
            placeholder="Greg · (612) 555-0123"
            className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
          />
        </Field>

        <Field label={<><Clock className="w-3 h-3 inline mr-1" />General Call</>}>
          <TimePicker
            value={callSheet.general_call}
            onChange={(v) => onField("general_call", v)}
            testIdPrefix="cs-general-call"
          />
        </Field>
        <Field label="Shooting Call">
          <TimePicker
            value={callSheet.shooting_call}
            onChange={(v) => onField("shooting_call", v)}
            testIdPrefix="cs-shooting-call"
          />
        </Field>
        <Field label="Est. Wrap">
          <TimePicker
            value={callSheet.est_wrap}
            onChange={(v) => onField("est_wrap", v)}
            testIdPrefix="cs-est-wrap"
          />
        </Field>
        <Field label={<><Sunrise className="w-3 h-3 inline mr-1" />Sunrise / <Sunset className="w-3 h-3 inline mx-1" />Sunset</>}>
          <div className="grid grid-cols-2 gap-1">
            <Input
              data-testid="cs-sunrise"
              value={callSheet.sunrise || ""}
              onChange={(e) => onField("sunrise", e.target.value)}
              placeholder="7:31am"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
            <Input
              data-testid="cs-sunset"
              value={callSheet.sunset || ""}
              onChange={(e) => onField("sunset", e.target.value)}
              placeholder="5:34pm"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
        </Field>
      </div>
    </section>
  );
}
